import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {
  FiX,
  FiChevronRight,
  FiChevronLeft,
  FiShield,
  FiTrendingUp,
  FiPieChart,
  FiLayers,
  FiMap,
  FiBriefcase,
  FiArrowDown,
  FiTarget,
  FiArrowRight,
  FiCheck
} = FiIcons;

const TOUR_STEPS = [
  {
    id: 'welcome',
    title: 'Welcome to WEALTH',
    tag: 'Personal CFO & Financial Modeling',
    icon: FiShield,
    color: 'blue',
    content:
      'WEALTH models your exact trajectory to Financial Independence. It blends zero-based cash routing, dynamic market stress-testing, and an actionable 11-step master roadmap into one unified financial engine.',
    highlights: [
      'Zero cloud sync — your financial data stays completely private on your device',
      'Realistic modeling with inflation, market downturns, and growth schedules',
      'Interactive 11-step roadmap guide with direct budget integration'
    ],
    visual: () => (
      <div className="bg-slate-900 p-6 md:p-8 rounded-3xl flex flex-col items-center justify-center min-h-[260px] shadow-2xl relative overflow-hidden text-center">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-blue-900/40 via-slate-900 to-slate-900"></div>
        <div className="w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center shadow-lg shadow-blue-500/50 relative z-10 mb-4">
          <SafeIcon icon={FiShield} className="text-white text-3xl" />
        </div>
        <h2 className="text-2xl font-black tracking-widest uppercase text-white relative z-10">WEALTH</h2>
        <p className="text-[10px] font-bold text-blue-400 uppercase tracking-[0.2em] mt-1 relative z-10">Personal CFO System</p>
        <div className="mt-4 flex gap-2 relative z-10">
          <span className="px-2.5 py-1 bg-white/10 rounded-full text-[9px] font-black uppercase text-slate-300">100% Private</span>
          <span className="px-2.5 py-1 bg-white/10 rounded-full text-[9px] font-black uppercase text-slate-300">CSV Portable</span>
          <span className="px-2.5 py-1 bg-white/10 rounded-full text-[9px] font-black uppercase text-slate-300">FI Roadmap</span>
        </div>
      </div>
    )
  },
  {
    id: 'modeling',
    title: 'Realistic Growth & Stress Tests',
    tag: 'Purchasing Power Reality',
    icon: FiTrendingUp,
    color: 'purple',
    content:
      'Traditional calculators assume static market returns. WEALTH lets you simulate real-life crashes and inflation so you know your true purchasing power before you retire.',
    highlights: [
      'Compare Nominal Wealth vs. Inflation-adjusted Real Purchasing Power',
      'Simulate a market crash year & severity to stress-test your timeline',
      'Switch between fixed monthly investing and expanding Linear Step models'
    ],
    visual: () => (
      <div className="bg-purple-50 p-5 md:p-6 rounded-3xl flex flex-col justify-center min-h-[260px] border border-purple-100 space-y-3">
        <div className="p-3.5 bg-white rounded-2xl shadow-sm border border-purple-100">
          <div className="flex justify-between items-center mb-1.5">
            <span className="text-[9px] font-black uppercase text-rose-500 tracking-widest">Crash Simulator</span>
            <span className="text-xs font-black text-rose-700">-40% Drop</span>
          </div>
          <div className="h-2 bg-rose-100 rounded-full overflow-hidden">
            <div className="h-full bg-rose-500 w-[40%]"></div>
          </div>
        </div>
        <div className="p-3.5 bg-white rounded-2xl shadow-sm border border-purple-100">
          <div className="flex justify-between items-center mb-1.5">
            <span className="text-[9px] font-black uppercase text-orange-500 tracking-widest">Inflation Adjuster</span>
            <span className="text-xs font-black text-orange-700">3.0% / yr</span>
          </div>
          <div className="h-2 bg-orange-100 rounded-full overflow-hidden">
            <div className="h-full bg-orange-500 w-[30%]"></div>
          </div>
        </div>
        <div className="p-3.5 bg-purple-600 text-white rounded-2xl shadow-md flex justify-between items-center">
          <div>
            <p className="text-[9px] font-black uppercase tracking-widest opacity-80">Real Target</p>
            <p className="text-xl font-black">$1,500,000</p>
          </div>
          <span className="text-[9px] font-bold bg-white/20 px-2 py-1 rounded-lg">Real Value</span>
        </div>
      </div>
    )
  },
  {
    id: 'budget',
    title: 'Zero-Based Routing & Budget',
    tag: 'Every Dollar Has a Job',
    icon: FiPieChart,
    color: 'emerald',
    content:
      'Incoming revenue lands in your Income Hub and is immediately allocated into fixed bills, investments, and discretionary buckets. Filter by account, normalize frequencies, or load curated life-stage presets.',
    highlights: [
      'Multi-frequency budgeting (Weekly, Bi-Weekly, Monthly, Yearly)',
      'Account allocation summary for Main Checking, Secondary Checking & Cash App',
      'Life scenario presets (Young Professional, Newly Married, Family, Peak Earning)'
    ],
    visual: () => (
      <div className="bg-emerald-50/50 p-5 md:p-6 rounded-3xl flex flex-col justify-center min-h-[260px] border border-emerald-100 space-y-2.5">
        <div className="flex justify-between items-center p-3 bg-white rounded-xl border border-emerald-100 shadow-sm">
          <div className="flex gap-2.5 items-center">
            <div className="w-7 h-7 bg-emerald-100 rounded-lg flex items-center justify-center text-emerald-600">
              <SafeIcon icon={FiBriefcase} className="text-xs" />
            </div>
            <span className="font-bold text-slate-900 text-xs">Income Hub</span>
          </div>
          <span className="font-black text-emerald-600 text-xs">+$6,500/mo</span>
        </div>
        <div className="flex justify-between items-center p-3 bg-white rounded-xl border border-rose-100 shadow-sm">
          <div className="flex gap-2.5 items-center">
            <div className="w-7 h-7 bg-rose-100 rounded-lg flex items-center justify-center text-rose-600">
              <SafeIcon icon={FiArrowDown} className="text-xs" />
            </div>
            <span className="font-bold text-slate-900 text-xs">Bills & Expenses</span>
          </div>
          <span className="font-black text-rose-600 text-xs">-$3,500/mo</span>
        </div>
        <div className="flex justify-between items-center p-3 bg-white rounded-xl border border-blue-100 shadow-sm">
          <div className="flex gap-2.5 items-center">
            <div className="w-7 h-7 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600">
              <SafeIcon icon={FiTarget} className="text-xs" />
            </div>
            <span className="font-bold text-slate-900 text-xs">Investments</span>
          </div>
          <span className="font-black text-blue-600 text-xs">+$3,000/mo</span>
        </div>
      </div>
    )
  },
  {
    id: 'roadmap',
    title: 'The Financial Roadmap Guide',
    tag: 'Your Step-by-Step Playbook',
    icon: FiMap,
    color: 'orange',
    content:
      'Follow an 11-step master strategy: from Starter Emergency Funds and Employer Matches, to Debt Eradication, Calculating your FI Number, and Tax Placement optimization.',
    highlights: [
      'Interactive tools for each step that directly sync with your budget',
      'Interactive visual diagram modal for high-level roadmap navigation',
      'Automatic calculation of your monthly investment gap and FI target'
    ],
    visual: () => (
      <div className="bg-orange-50 p-5 md:p-6 rounded-3xl flex flex-col justify-center min-h-[260px] border border-orange-100">
        <div className="space-y-3.5 w-full max-w-sm mx-auto pl-4 border-l-2 border-orange-200 relative">
          <div className="relative">
            <div className="absolute -left-[21px] top-1 w-3 h-3 bg-emerald-500 rounded-full ring-4 ring-orange-50"></div>
            <p className="text-[8px] font-black uppercase text-emerald-600 tracking-widest">Step 1</p>
            <p className="text-xs font-bold text-slate-900">3-Month Cash Flow Baseline</p>
          </div>
          <div className="relative">
            <div className="absolute -left-[21px] top-1 w-3 h-3 bg-blue-500 rounded-full ring-4 ring-orange-50"></div>
            <p className="text-[8px] font-black uppercase text-blue-600 tracking-widest">Step 2</p>
            <p className="text-xs font-bold text-slate-900">Strategic Parameters & Goal Bar</p>
          </div>
          <div className="relative">
            <div className="absolute -left-[21px] top-1 w-3 h-3 bg-purple-500 rounded-full ring-4 ring-orange-50"></div>
            <p className="text-[8px] font-black uppercase text-purple-600 tracking-widest">Step 7</p>
            <p className="text-xs font-bold text-slate-900">Calculate FI Number (25x / 28x)</p>
          </div>
        </div>
      </div>
    )
  }
];

const WelcomeTourModal = ({ isOpen, onClose, onStartRoadmap }) => {
  const [currentStep, setCurrentStep] = useState(0);

  if (!isOpen) return null;
  const step = TOUR_STEPS[currentStep];
  const isLast = currentStep === TOUR_STEPS.length - 1;

  const handleNextOrFinish = () => {
    if (isLast) {
      onClose();
      if (onStartRoadmap) onStartRoadmap();
    } else {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const handleJumpToRoadmap = () => {
    onClose();
    if (onStartRoadmap) onStartRoadmap();
  };

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center p-3 md:p-6 bg-slate-900/75 backdrop-blur-md">
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className="bg-white w-full max-w-4xl rounded-[2rem] md:rounded-[2.5rem] shadow-2xl relative overflow-hidden flex flex-col md:flex-row min-h-[520px] max-h-[92vh]"
      >
        {/* Top Progress Indicator */}
        <div className="absolute top-0 left-0 w-full h-1.5 bg-slate-100 z-50">
          <motion.div
            className="h-full bg-gradient-to-r from-blue-600 to-indigo-600"
            initial={{ width: 0 }}
            animate={{ width: `${((currentStep + 1) / TOUR_STEPS.length) * 100}%` }}
          />
        </div>

        {/* Left Side: Content & Action */}
        <div className="w-full md:w-1/2 p-6 md:p-10 flex flex-col justify-between z-10 relative overflow-y-auto custom-scrollbar">
          <div>
            <div className="flex justify-between items-start mb-6">
              <div>
                <span className="text-[9px] font-black uppercase text-blue-600 tracking-[0.2em] mb-1 block">
                  {step.tag}
                </span>
                <h2 className="text-lg font-black text-slate-900 tracking-tight">
                  Overview {currentStep + 1} of {TOUR_STEPS.length}
                </h2>
              </div>
              <button
                onClick={onClose}
                aria-label="Close welcome"
                className="p-2 text-slate-400 hover:text-slate-600 rounded-full transition-colors"
              >
                <SafeIcon icon={FiX} className="text-xl" />
              </button>
            </div>

            <AnimatePresence mode="wait">
              <motion.div
                key={currentStep}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-4"
              >
                <div className={`w-12 h-12 bg-${step.color}-50 text-${step.color}-600 rounded-2xl flex items-center justify-center shadow-inner`}>
                  <SafeIcon icon={step.icon} className="text-2xl" />
                </div>

                <div>
                  <h3 className="text-2xl font-black text-slate-900 mb-2 tracking-tight leading-tight">
                    {step.title}
                  </h3>
                  <p className="text-slate-600 text-xs md:text-sm leading-relaxed font-medium">
                    {step.content}
                  </p>
                </div>

                {step.highlights && (
                  <ul className="space-y-2 pt-2">
                    {step.highlights.map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-xs font-semibold text-slate-700">
                        <SafeIcon icon={FiCheck} className="text-emerald-500 text-sm mt-0.5 flex-shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </motion.div>
            </AnimatePresence>
          </div>

          <div className="space-y-3 mt-8 pt-4 border-t border-slate-100">
            <div className="flex gap-2.5">
              <button
                disabled={currentStep === 0}
                onClick={() => setCurrentStep((prev) => prev - 1)}
                className={`w-12 py-3.5 rounded-xl font-black text-xs uppercase tracking-widest flex items-center justify-center transition-all ${
                  currentStep === 0 ? 'bg-slate-50 text-slate-300 cursor-not-allowed' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
                title="Previous"
              >
                <SafeIcon icon={FiChevronLeft} />
              </button>
              <button
                onClick={handleNextOrFinish}
                className="flex-1 py-3.5 bg-slate-900 text-white rounded-xl font-black text-xs uppercase tracking-widest flex items-center justify-center gap-2 hover:bg-black transition-all shadow-xl shadow-slate-200"
              >
                <span>{isLast ? 'Launch Roadmap Guide' : 'Next Overview'}</span>
                <SafeIcon icon={isLast ? FiArrowRight : FiChevronRight} />
              </button>
            </div>

            {!isLast && (
              <button
                onClick={handleJumpToRoadmap}
                className="w-full text-center text-[10px] font-black uppercase tracking-widest text-blue-600 hover:text-blue-800 transition-colors py-1"
              >
                Skip Overview & Open Roadmap Guide →
              </button>
            )}
          </div>
        </div>

        {/* Right Side: Visual Component */}
        <div className="hidden md:flex w-full md:w-1/2 bg-slate-50 border-l border-slate-100 items-center justify-center p-8 relative">
          <button
            onClick={onClose}
            aria-label="Close modal"
            className="absolute top-6 right-6 p-2.5 bg-white border border-slate-200 rounded-full text-slate-400 hover:text-slate-600 shadow-sm transition-colors z-50"
          >
            <SafeIcon icon={FiX} className="text-lg" />
          </button>
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="w-full"
            >
              {step.visual && step.visual()}
            </motion.div>
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
};

export default WelcomeTourModal;